const convertHTMLObjToArrayForKpp = (htmlObj) => {
  return Object.entries(htmlObj).map(([key, html]) => {
    return { id: key, ...parseHTMLStringForKpp(html) };
  });
}

const parseHTMLStringForKpp = (htmlString) => {
  const name = (htmlString.match(/<[^>]+data-id="name"[^>]*>([^<]+)<\/[^>]+>/) || [])[1] ?? '';
  const description = (htmlString.match(/<[^>]+data-id="description"[^>]*>\s*([^<]+)/s) || [])[1]?.trim() ?? '';
  const rating = (htmlString.match(/<[^>]+data-id="ratingValue"[^>]*>([^<]+)<\/[^>]+>/) || [])[1] ?? '';
  const reviewCount = (htmlString.match(/<[^>]+data-id="reviewCount"[^>]*>([^<]+)<\/[^>]+>/) || [])[1] ?? '';
  const address = (htmlString.match(/<[^>]+data-id="address"[^>]*>([^<]+)<\/[^>]+>/) || [])[1] ?? '';
  const image = (htmlString.match(/<[^>]+srcset="([^"]+)"[^>]*>/) || [])[1] ?? '';

  return { name, description, rating, reviewCount, address, image };						
}

const generateJsonLdForKpp = (kppData, site, placeName = '') => {
  return kppData.map(item => {
    const aggregateRating = item.rating ? {
      "@type": "AggregateRating",
      ratingValue: item.rating,
      reviewCount: item.reviewCount
    } : undefined;

    const address = {
      "@type": "PostalAddress",
      addressLocality: item.address || placeName,
      addressCountry: "US"
    };

    const logo = item.image ? {
      "@type": "ImageObject",
      contentUrl: item.image
    } : undefined;
    
    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      additionalType: site === 'demolition' ? "https://schema.org/HomeAndConstructionBusiness" : "https://schema.org/WasteManagement",
      name: item.name,
      description: item.description,
      ...(aggregateRating && { aggregateRating }),
      address,
      ...(logo && { logo })
    };
  });
};

const generateTypicalPricesSchema = (typicalPricesChart, placeName) => {
  if (!typicalPricesChart?.rows?.length) return null

  const { rows, city_name } = typicalPricesChart
  const displayCity = city_name || placeName || 'your area'

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Dumpster Rental in ${displayCity}`,
    "areaServed": {
      "@type": "City",
      "name": displayCity
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Dumpster Rental Pricing",
      "itemListElement": rows.map(row => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `${row.size_label} dumpster`
        },
        "priceSpecification": {
          "@type": "PriceSpecification",
          "minPrice": Math.round(row.low_price),
          "maxPrice": Math.round(row.high_price),
          "priceCurrency": "USD"
        }
      }))
    }
  }
}

const generateJsonLdForPpp = (pppData, pppPrices, buildMode, placeName = '') => {
  const EXACT_SIZES = { ppp10: 10, ppp20: 20, ppprolloff: 30 }
  const exactSize = EXACT_SIZES[buildMode] || null

  const exactPricesPerPro = []
  const allExactPrices = []
  const nearbySizeMap = {}

  pppData.forEach(pro => {
    const services = pppPrices[String(pro.id)] || []
    const proExactPrices = []

    services.forEach(s => {
      if (s.size === exactSize) {
        const price = parseFloat(s.price)
        proExactPrices.push(price)
        allExactPrices.push(price)
      } else if (s.size !== null) {
        if (!nearbySizeMap[s.size]) nearbySizeMap[s.size] = []
        nearbySizeMap[s.size].push(parseFloat(s.price))
      }
    })

    if (proExactPrices.length > 0) {
      exactPricesPerPro.push({ name: pro.name, price: Math.min(...proExactPrices) })
    }
  })

  const hasExactPrices = exactPricesPerPro.length > 0
  const hasNearbyPrices = Object.keys(nearbySizeMap).length > 0

  const ratedPros = pppData.filter(p => p.rating && p.reviewCount)
  const totalReviews = ratedPros.reduce((sum, p) => sum + Number(p.reviewCount), 0)
  const weightedRating = totalReviews > 0
    ? ratedPros.reduce((sum, p) => sum + Number(p.rating) * Number(p.reviewCount), 0) / totalReviews
    : null
  const aggregateRating = weightedRating ? {
    "@type": "AggregateRating",
    ratingValue: weightedRating.toFixed(1),
    reviewCount: totalReviews,
  } : undefined

  const productName = exactSize
    ? `${exactSize} Yard Dumpster Rental in ${placeName}`
    : `Dumpster Rental in ${placeName}`

  const isRelatedTo = Object.entries(nearbySizeMap).map(([size, prices]) => ({
    "@type": "Product",
    name: `${size} Yard Dumpster Rental in ${placeName}`,
    offers: {
      "@type": "AggregateOffer",
      lowPrice: Math.min(...prices).toFixed(2),
      highPrice: Math.max(...prices).toFixed(2),
      offerCount: prices.length,
      priceCurrency: "USD",
    }
  }))

  // Case A: exact-size prices available → Product + AggregateOffer
  if (hasExactPrices) {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: productName,
      ...(aggregateRating && { aggregateRating }),
      offers: {
        "@type": "AggregateOffer",
        lowPrice: Math.min(...allExactPrices).toFixed(2),
        highPrice: Math.max(...allExactPrices).toFixed(2),
        offerCount: exactPricesPerPro.length,
        priceCurrency: "USD",
        offers: exactPricesPerPro.map(pro => ({
          "@type": "Offer",
          seller: { "@type": "LocalBusiness", name: pro.name },
          price: pro.price.toFixed(2),
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
        })),
      },
      ...(isRelatedTo.length > 0 && { isRelatedTo }),
    }
  }

  // Case B: no exact prices but nearby sizes have prices → quote-only + isRelatedTo
  if (hasNearbyPrices) {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: productName,
      ...(aggregateRating && { aggregateRating }),
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        priceCurrency: "USD",
      },
      ...(isRelatedTo.length > 0 && { isRelatedTo }),
    }
  }

  // Case C: no prices at all → one Offer per pro, all InStock, no prices
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productName,
    ...(aggregateRating && { aggregateRating }),
    offers: pppData.map(pro => ({
      "@type": "Offer",
      seller: { "@type": "LocalBusiness", name: pro.name },
      availability: "https://schema.org/InStock",
      priceCurrency: "USD",
    })),
  }
}

const generateJsonLdForPppConstruction = (pppData, pppPrices, placeName = '') => {
  const [cityName, addressRegion] = placeName.split(', ')

  // For each size, track the pro with the lowest price
  const sizeMap = {}

  pppData.forEach(pro => {
    const services = pppPrices[String(pro.id)] || []
    services.forEach(s => {
      if (s.size === null) return
      const price = parseFloat(s.price)
      if (!sizeMap[s.size] || price < sizeMap[s.size].price) {
        sizeMap[s.size] = { proName: pro.name, price }
      }
    })
  })

  const sortedSizes = Object.keys(sizeMap).map(Number).sort((a, b) => a - b)

  const itemListElement = sortedSizes.map(size => {
    const { proName, price } = sizeMap[size]
    const hasPrice = !isNaN(price) && price > 0
    return {
      "@type": "Offer",
      name: `${size}-Yard Construction Dumpster Rental`,
      ...(hasPrice && { price: price.toFixed(2), priceCurrency: "USD" }),
      availability: "https://schema.org/InStock",
      businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
      itemOffered: {
        "@type": "Product",
        name: `${size}-Yard Dumpster for Construction Debris`,
      },
      offeredBy: {
        "@type": "LocalBusiness",
        name: proName,
      },
    }
  })

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Construction Dumpster Rental in ${placeName}`,
    description: `Compare construction dumpster rental options from local providers in ${cityName || placeName}. Available sizes may include 10-yard, 20-yard, and 30-yard dumpsters depending on the provider and debris type.`,
    serviceType: "Construction Dumpster Rental",
    areaServed: {
      "@type": "City",
      name: cityName || placeName,
      address: {
        "@type": "PostalAddress",
        ...(addressRegion && { addressRegion }),
        addressCountry: "US",
      },
    },
    ...(itemListElement.length > 0 && {
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Construction Dumpster Rental Options",
        itemListElement,
      },
    }),
  }
}


// then we made this function dynamic for dumpster and demolition, when mark has the demolition schema
const generateJsonLdForHome = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Hometown Dumpster Rental",
  "alternateName": "Hometown Local",
  "url": "https://www.hometowndumpsterrental.com/",
  "logo": "https://www.hometowndumpsterrental.com/images/logo.png",
  "description": "Founded in 2008, Hometown Dumpster Rental is a nationwide marketplace making it easy to compare prices and book local dumpster rental and junk removal services.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "2153 Wealthy St., Suite 117",
    "addressLocality": "Grand Rapids",
    "addressRegion": "MI",
    "postalCode": "49506",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-877-994-6645",
    "contactType": "customer service",
    "areaServed": "US",
    "availableLanguage": "en"
  },
  "sameAs": [
    "https://www.facebook.com/HometownDumpsterRental",
    "https://x.com/hometowndumpstr",
"https://www.bbb.org/us/mi/grand-rapids/profile/dumpster-rentals/hometown-dumpster-rental-0372-90065857",
    "https://www.trustpilot.com/review/www.hometowndumpsterrental.com",
"https://www.youtube.com/c/HometownDumpsterRental"
  ],
  "brand": {
    "@type": "Brand",
    "name": "Hometown Dumpster Rental™",
    "slogan": "Low Prices, Easy Booking."
  }
});

const formatBookingJson = (data, placeId = null) => {
  let newFormData = data
  if (typeof data === 'string') {
    try {
      newFormData = JSON.parse(data);
    } catch (error) {
      console.error('Error parsing JSON:', error);
    }
  }
  const initialValues = [
    { key: 'wasteType', value: [+newFormData.wasteType] },
    { key: 'deliveryAddress', value: (newFormData.odf_street && newFormData.odf_street2) ? newFormData.odf_street + ', ' + newFormData.odf_street2 : ''},
    { key: 'fullFormData', value: newFormData},
  ]
  localStorage.setItem('onlineBookingData',JSON.stringify({ placeId: placeId, baseServiceId: "62a9f2cd61706a135071a7e3", skip: 0, limit: 21, initialValues: initialValues, finalDataForBooking: null }))
  return newFormData;
}

// Build breadcrumb schema Json-LD
const buildBreadcrumbSchema = (page) => {
  const breadcrumb = page.breadcrumb || [];

  // Only include links with both url and label
  const validLinks = breadcrumb.filter(
    link => link?.url && link?.label
  );

  if (validLinks.length === 0) return null;

  const items = validLinks.map((link, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: link.label,
    item: link.url
  }));

  return {
    "@type": "BreadcrumbList",
    itemListElement: items
  };
};

// Build profiles schema Json-LD
const buildWasteManagementServiceSchema = (page, reviewsData = []) => {
  if (!page?.profile) return null;

  const profile = page.profile;

  const schema = {
    "@type": "HomeAndConstructionBusiness",
    "@id": `${page.env?.domain || ''}/${page.url}#business`,
    name: profile.name,
    url: `${page.env?.domain || ''}/${page.url}`,
  };

  // Phone
  if (profile.phone) {
    schema.telephone = profile.phone;
  }

  const SERVICE_PRIORITY = [
        'Dumpster Rental',
        'Junk Removal',
        'Commercial Trash Service',
    ];

  const getPrimaryService = (page) => {
      const services = page.services?.[0] || [];

      const serviceNames = services.map(s => s.name);
      return SERVICE_PRIORITY.find(p => serviceNames.includes(p)) || 'Dumpster Rental';
  };

  const getSeoCities = (page, limit = 3) => {
        const cities = page.areas?.flatMap(area => area.places || []) || [];
        
        return cities.slice(0, limit).map(city => city.split(',')[0]);
    };

  // Description
  if (profile.name && page.services?.length > 0 && page.areas?.length > 0) {
    const service = getPrimaryService(page);
    const cities = getSeoCities(page).join(', ');

    switch (service) {
      case 'Junk Removal':
        schema.description = `Get affordable cleanouts and junk pickup from ${profile.name}. Full-service junk removal in ${cities} and nearby.`;
        break;

      case 'Commercial Trash Service':
        schema.description = `Get affordable commercial trash pickup from ${profile.name} in ${cities} and nearby. Get a quote and save on monthly rates.`;
        break;

      case 'Dumpster Rental':
      default:
        schema.description = `Get dumpster rental pricing and availability for ${profile.name}. Easy online booking & same day/next day delivery to ${cities} and nearby.`;
        break;
    }
  }

  // Logo url
  if (profile.logo?.path) {
    schema.image = profile.logo.path;
  }

  // Address
  if (profile.add_c && profile.add_av && profile.zip) {
    schema.address = {
      "@type": "PostalAddress",
      addressLocality: profile.add_c,
      addressRegion: profile.add_av,
      postalCode: profile.zip,
      addressCountry: "US",
    };

    if (!profile.hide_address && profile.address) {
      schema.address.streetAddress = profile.address;
    }
  }

  // Area Served 
  if (page.areas?.length > 0) {
    const cities = [];

    page.areas.forEach(area => {
      area.places?.forEach(place => {
        const city = place.split(',')[0]?.trim();
        if (city && cities.length < 10) {
          cities.push({
            "@type": "City",
            name: city,
          });
        }
      });
    });

    if (cities.length > 0) {
      schema.areaServed = cities;
    }
  } 
  

  // Offer Catalog (Dumpster Services)
  if (page.serviceSeekerData?.defaultServices?.length > 0) {

    const buildServiceDescription = (service) => {
      const weightDetail = service.details?.find(d => d.text1?.includes('ton'));
      const dayDetail = service.details?.find(d => d.text1?.includes('day'));

      const weightTerm = weightDetail?.text3 || weightDetail?.text1?.split('. <br>')[0];
      const dayTerm = dayDetail?.text3 || dayDetail?.text1?.split('. <br>')[0];

      if (!weightTerm || !dayTerm) return '';

      return `Includes up to ${dayTerm.replace('included', '').trim()} and ${weightTerm.replace('included', '').trim()} of waste`;
    };


    schema.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: "Waste Management Services",
      itemListElement: [
        {
          "@type": "OfferCatalog",
          name: "Dumpster Rental",
          itemListElement: page.serviceSeekerData.defaultServices.map(service => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: service.title + ' Rental',
              description: buildServiceDescription(service)
            },
            priceSpecification: {
              "@type": "PriceSpecification",
              price: service.price,
              priceCurrency: "USD",
              description: "Starting at price"
            }
          }))
        }
      ]
    };
  }


  // Aggregate Rating
  if (reviewsData?.ratingValue && reviewsData?.reviewCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: reviewsData.ratingValue,
      reviewCount: reviewsData.reviewCount,
      bestRating: "5",
      worstRating: "1",
    };
  }

  // Reviews (top N)
  if (reviewsData?.reviews?.length > 0) {
    schema.review = reviewsData.reviews.slice(0, 5);
  }

  return schema;
};



const manualGoogleEvents = (eventName) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: eventName
    });
  }
}

const formatData = (data) => {
  let address = data.find(element => element.key === 'fullFormData').value
  if(address) {
    address = JSON.parse(address)
    let addressObj = {}
    address.forEach(e => addressObj[e[0]] = e[1])
    const wasteType = data.find(element => element.key === 'wasteType').value
    const dumpsterSize = data.find(e => e.key === 'dumpsterSize')?.value
    const obj = {
      "odf_country": addressObj.country,
      "odf_locality": addressObj.locality,
      "odf_place_id": addressObj.place_id,
      "odf_postal_code": addressObj.postal_code,
      "odf_state": addressObj.state,
      "odf_street": addressObj.street,
      "odf_street2": addressObj.street2,
      "wasteType": wasteType,
    }
    if (dumpsterSize) {
      obj.dumpsterSize = dumpsterSize;
    }
    return obj;
  }
}

const getFormDataForStorage = () => {
  let obj = {};
  if(typeof window !== 'undefined'){ 
    const localData = JSON.parse(localStorage.getItem('onlineBookingData'));
    if(localData){
      let populatedData = {};
      if (localData.finalDataForBooking) {
        try {
          populatedData = formatData(localData.finalDataForBooking);
        } catch {}
        // Si finalDataForBooking no tiene datos válidos, caemos a initialValues
        if (!populatedData?.wasteType || !populatedData?.odf_postal_code) {
          populatedData = localData.initialValues?.find(data => data.key === 'fullFormData')?.value;
        }
      } else {
        populatedData = localData.initialValues?.find(data => data.key === 'fullFormData')?.value
      }
      if (populatedData?.wasteType && populatedData?.odf_postal_code) {
        const wasteTypeValue = Array.isArray(populatedData.wasteType) ? populatedData.wasteType[0] : populatedData.wasteType;
        obj.wasteType = wasteTypeValue;
        delete populatedData.wasteType;
        delete populatedData.dumpsterSize;
        obj.addressData = { ...populatedData };
      }
    }
  }
  return obj;
}

const getInitialFormValues = () => {
  const localData = getFormDataForStorage();
  if (localData?.addressData && localData?.wasteType) {
    const allData = {
      ...localData.addressData,
      wasteType: localData.wasteType
    };
    const keyValueArray = Object.entries(allData).map(([key, value]) => ({
      key,
      value
    }));
    return keyValueArray;
  }
  return [];
}

const trackProCardsViews = (eventPrefix, className, startCounter = 0, customerView, sponsored) => {
  if (!window.dataLayer) {
    console.error("dataLayer is not available.");
    return;
  }

  const elements = document.getElementsByClassName(className);
  if (!elements || elements.length === 0) {
    return;
  }
  const viewedServiceIds = new Set(); // track each unique service card
  const viewedProIds = new Set();     // track unique pros for customer views

  if (startCounter > 0) {
    [...elements].slice(0, startCounter).forEach(el => {
      const proId = el.dataset.proid;
      if (proId) viewedProIds.add(proId);
    });
  }

  let counter = startCounter;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const proId = el.dataset.proid;
          const serviceId = el.dataset.serviceid;
          // main event — fire once per serviceId (each card)
          if (!viewedServiceIds.has(serviceId)) {
            viewedServiceIds.add(serviceId);
            counter++;

            const eventName = eventPrefix + counter;
            const eventData = {
              event: eventName,
              proid: proId,
            };
            eventData[eventName] = proId;

            window.dataLayer.push(eventData);
          }

          // customer view — fire once per proId
          if (customerView && !viewedProIds.has(proId)) {
            viewedProIds.add(proId);
            const foundIndex = viewedProIds.size;
            const eventGtaName = (sponsored ? "sponsoredProServiceVisible" : "proServiceVisible") + foundIndex;
            const eventGtaData = {
              event: eventGtaName,
              proid: proId,
            };
            eventGtaData[eventGtaName] = proId;

            window.dataLayer.push(eventGtaData);
          }

          obs.unobserve(el); // stop tracking once it has been seen
        }
      });
    },
    {
      root: null,
      threshold: 0.3, // 30% visible to trigger
    }
  );

  const elementsToProcess = [...elements].slice(startCounter);
  elementsToProcess.forEach(el => observer.observe(el));
}

const trackSimpleEvent = (eventName, proId) => {
  window.dataLayer = window.dataLayer || [];
  
  window.dataLayer.push({
    event: eventName,
    proid: String(proId),
    timestamp: Date.now(),
  });
};


const trackProCardsViewsOnViewport = (eventPrefix, className, startCounter = 0) => {
  if (!window.dataLayer) {
    console.error("dataLayer is not available.");
    return;
  }

  const elements = document.getElementsByClassName(className);
  if (!elements?.length) return;

  const viewed = new Set();
  let counter = startCounter;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const proId = el.dataset.proid;
          if (!viewed.has(proId)) {
            viewed.add(proId);
            counter++;

            const eventName = eventPrefix + counter;
            const eventData = {
              event: eventName,
              proid: proId,
            };
            eventData[eventName] = proId;

            window.dataLayer.push(eventData);
          }

          obs.unobserve(el);
        }
      });
    },
    {
      root: null,
      threshold: 0.3, // 30% visible
    }
  );

  const elementsToProcess = [...elements].slice(startCounter)
  if (elementsToProcess?.length > 0) {
    elementsToProcess.forEach(el => observer.observe(el));
  }
};

const trackBookingSubmission = (proId) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: 'bookingSubmission',
      proid: proId,
      'bookingSubmission': proId
    });
  }
}

const createAddressEvent = async (addressObject, proId) => {
  try {
    if (process.env.GATSBY_EVENTS_API_URL) {
      const source = window.location.origin;
      const url = window.location.href;
      let sessionId = localStorage.getItem('sessionId');
      if (!sessionId) {
        const auxCode = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2)
        localStorage.setItem('sessionId', auxCode)
        sessionId = auxCode;
      }
      const event = {
        eventType: "SC",
        eventDescription: !addressObject.hasNoCity ? "Address Selected" : "No city selected",
        eventData: {
          source,
          sessionId,
          url,
          ...addressObject,
        }
      }
      if (proId) {
        event.proId = proId;
      }

      const response = await fetch(
        `${process.env.GATSBY_EVENTS_API_URL}/events`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(event),
        }
      );
      return await response.json();
    }
    return;
  } catch (error) {
    console.error('Error creating address event:', error);
  }
}

module.exports = { convertHTMLObjToArrayForKpp, generateJsonLdForKpp, generateTypicalPricesSchema, generateJsonLdForPpp, generateJsonLdForPppConstruction, formatBookingJson, manualGoogleEvents, formatData, getFormDataForStorage, getInitialFormValues, trackProCardsViews, trackSimpleEvent, trackBookingSubmission, createAddressEvent, trackProCardsViewsOnViewport, buildBreadcrumbSchema, buildWasteManagementServiceSchema, generateJsonLdForHome };
